/**
 * Apply color filters to a short video via canvas + MediaRecorder.
 * Returns the original file when edits are neutral or re-encode is unavailable.
 */

import { readVideoDuration } from './readVideoDuration';
import { buildColorCssFilter, hasColorEdits } from './mediaColorFilter';

export const VIDEO_EDIT_DEFAULTS = {
  brightness: 0,
  contrast: 0,
  saturation: 0,
  warmth: 0,
};

function pickRecorderMimeType() {
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4',
  ];
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
    return '';
  }
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || '';
}

function attachVideoSource(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.src = url;
    video.onloadeddata = () => resolve({ video, url });
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read that video file.'));
    };
  });
}

/**
 * @param {File|Blob} file
 * @param {{ brightness?: number, contrast?: number, saturation?: number, warmth?: number }} [edits]
 * @param {{ maxSeconds?: number, durationHint?: number|null, onProgress?: (ratio: number) => void }} [options]
 * @returns {Promise<File>}
 */
export async function applyVideoEdits(
  file,
  { brightness = 0, contrast = 0, saturation = 0, warmth = 0 } = {},
  { maxSeconds = 15, durationHint = null, onProgress } = {}
) {
  if (!file) throw new Error('No video selected.');

  const edits = { brightness, contrast, saturation, warmth };
  if (!hasColorEdits(edits)) {
    return file instanceof File
      ? file
      : new File([file], `clip-${Date.now()}.webm`, { type: file.type || 'video/webm' });
  }

  if (typeof MediaRecorder === 'undefined' || typeof HTMLCanvasElement === 'undefined') {
    throw new Error('Video adjustments are not supported in this browser. Use Skip to keep the original clip.');
  }

  const mimeType = pickRecorderMimeType();
  if (!mimeType) {
    throw new Error('Video adjustments are not supported in this browser. Use Skip to keep the original clip.');
  }

  let knownDuration = Number.isFinite(durationHint) && durationHint > 0 ? durationHint : null;
  if (!knownDuration) {
    try {
      knownDuration = await readVideoDuration(file, {
        fallbackSeconds: maxSeconds,
        timeoutMs: 5000,
      });
    } catch {
      knownDuration = maxSeconds;
    }
  }

  const duration = Math.min(knownDuration, maxSeconds + 0.5);
  const { video, url } = await attachVideoSource(file);

  try {
    // Ensure dimensions are available
    if (!video.videoWidth) {
      await new Promise((resolve) => {
        if (video.videoWidth) {
          resolve();
          return;
        }
        video.onloadeddata = () => resolve();
        video.onerror = () => resolve();
      });
    }

    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not prepare video editor.');

    ctx.filter = buildColorCssFilter(edits);

    video.muted = false;
    video.currentTime = 0;
    await video.play().catch(() => {});

    const canvasStream = canvas.captureStream(30);
    try {
      if (typeof video.captureStream === 'function') {
        const mediaStream = video.captureStream();
        mediaStream.getAudioTracks().forEach((track) => canvasStream.addTrack(track));
      }
    } catch {
      /* audio optional */
    }

    const recorder = new MediaRecorder(canvasStream, { mimeType });
    const chunks = [];
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) chunks.push(event.data);
    };

    const recorded = new Promise((resolve, reject) => {
      recorder.onerror = () => reject(new Error('Could not apply video adjustments.'));
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: recorder.mimeType || mimeType });
        resolve(blob);
      };
    });

    recorder.start(200);

    const started = performance.now();
    await new Promise((resolve, reject) => {
      const draw = () => {
        try {
          ctx.drawImage(video, 0, 0, width, height);
        } catch (err) {
          reject(err);
          return;
        }
        if (onProgress && duration > 0) {
          onProgress(Math.min(1, video.currentTime / duration));
        }
        if (
          video.ended ||
          video.currentTime >= duration ||
          performance.now() - started > (maxSeconds + 3) * 1000
        ) {
          resolve();
          return;
        }
        requestAnimationFrame(draw);
      };

      video.onended = () => resolve();
      requestAnimationFrame(draw);
    });

    video.pause();
    if (recorder.state !== 'inactive') recorder.stop();
    canvasStream.getTracks().forEach((t) => t.stop());

    const blob = await recorded;
    if (!blob.size) throw new Error('Adjusted video was empty. Please try again.');

    const ext = (recorder.mimeType || mimeType).includes('mp4') ? 'mp4' : 'webm';
    const type = recorder.mimeType || mimeType;
    return new File([blob], `clip-edited-${Date.now()}.${ext}`, { type });
  } finally {
    video.pause();
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(url);
  }
}
